import {
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  getAccount,
  getAssociatedTokenAddress,
} from '@solana/spl-token';
import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import { CWY_MINT_ADDRESS, CWY_TREASURY_WALLET, cwyAmountToRaw } from '../config/cwyConfig';
import {
  assertSufficientSolForDeposit,
  formatInsufficientCwyBalanceError,
  formatMissingCwyBalanceError,
  getWalletCwyBalance,
} from './cwyTokenBalance';

export async function buildCwyDepositTransaction(params: {
  connection: Connection;
  sender: PublicKey;
  amountCwy: number;
}): Promise<Transaction> {
  if (!CWY_MINT_ADDRESS || !CWY_TREASURY_WALLET) {
    throw new Error('CWY vault is not configured');
  }

  const walletInfo = await getWalletCwyBalance(params.connection, params.sender);
  assertSufficientSolForDeposit(walletInfo.solBalance);

  if (!walletInfo.tokenAccount || walletInfo.balance <= 0) {
    throw new Error(formatMissingCwyBalanceError(params.sender.toBase58()));
  }
  if (params.amountCwy > walletInfo.balance) {
    throw new Error(formatInsufficientCwyBalanceError(walletInfo.balance, params.amountCwy));
  }

  const mint = new PublicKey(CWY_MINT_ADDRESS);
  const treasury = new PublicKey(CWY_TREASURY_WALLET);
  const senderAta = walletInfo.tokenAccount;
  const treasuryAta = await getAssociatedTokenAddress(mint, treasury);

  const { blockhash, lastValidBlockHeight } = await params.connection.getLatestBlockhash();
  const transaction = new Transaction({
    blockhash,
    lastValidBlockHeight,
    feePayer: params.sender,
  });

  try {
    await getAccount(params.connection, treasuryAta);
  } catch {
    transaction.add(
      createAssociatedTokenAccountInstruction(params.sender, treasuryAta, treasury, mint)
    );
  }

  transaction.add(
    createTransferInstruction(
      senderAta,
      treasuryAta,
      params.sender,
      BigInt(cwyAmountToRaw(params.amountCwy))
    )
  );

  return transaction;
}
